var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { useClasses, useLayoutProps, useProps, useSkeletonLoader, } from '@hawkins/helpers';
import { HawkinsGlobalClass, useTheme } from '@hawkins/styles';
import { useHeadingStyles } from './heading.styles';
var DEFAULT_LEVEL = 2;
/**
 * Utility React hook that returns the height to use for a Skeleton loader for the <Heading /> component
 * @private
 */
export function useHeadingLoaderProps(props) {
    var _a = (props !== null && props !== void 0 ? props : {}).level, level = _a === void 0 ? DEFAULT_LEVEL : _a;
    var theme = useTheme();
    return {
        height: (parseInt(theme.hawkins.heading["fontSize".concat(level)].toString(), 10) || 0) *
            (theme.hawkins.heading.lineHeightRegular || 0),
        variant: 'text',
        width: '100%',
    };
}
var displayName = 'Heading';
export var Heading = React.forwardRef(function (_a, ref) {
    var _b;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _c = useProps(displayName, props), align = _c.align, classesProp = _c.classes, className = _c.className, color = _c.color, _d = _c.colorVariant, colorVariant = _d === void 0 ? 'default' : _d, _e = _c.component, Component = _e === void 0 ? 'h1' : _e, gutterBottom = _c.gutterBottom, gutterTop = _c.gutterTop, layout = _c.layout, _f = _c.level, level = _f === void 0 ? DEFAULT_LEVEL : _f, loaderProps = _c.loaderProps, loading = _c.loading, _g = _c.noWrap, noWrap = _g === void 0 ? false : _g, styleProp = _c.style, restProps = __rest(_c, ["align", "classes", "className", "color", "colorVariant", "component", "gutterBottom", "gutterTop", "layout", "level", "loaderProps", "loading", "noWrap", "style"]);
    // Styles
    var baseClasses = useHeadingStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var baseStyle = React.useMemo(function () { return (__assign({ textAlign: align }, styleProp)); }, [align, styleProp]);
    var topLevelLayoutProps = React.useMemo(function () { return ({ color: color, marginBottom: gutterBottom, marginTop: gutterTop }); }, [color, gutterBottom, gutterTop]);
    var style = useLayoutProps({ layout: layout, style: baseStyle, topLevelLayoutProps: topLevelLayoutProps });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, classes["level".concat(level)], (_b = {},
        _b[HawkinsGlobalClass.TRUNCATE] = noWrap,
        _b[classes.rootColorDisabled] = colorVariant === 'disabled',
        _b[classes.rootColorFaded] = colorVariant === 'faded',
        _b[classes.rootColorReverse] = colorVariant === 'reverse',
        _b));
    // Content
    var skeletonLoaderProps = useHeadingLoaderProps({ level: level });
    var skeletonLoaderNode = useSkeletonLoader({
        loading: loading,
        style: style,
        loaderProps: __assign(__assign({}, skeletonLoaderProps), loaderProps),
    });
    if (skeletonLoaderNode)
        return skeletonLoaderNode;
    return (_jsx(Component, __assign({ className: rootClassName, ref: ref, style: style }, restProps, { children: children })));
});
Heading.displayName = displayName;
